'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface TeamMember {
  id: number;
  name: string;
  nameArabic: string;
  role: string;
  roleArabic: string;
  image: string;
  alt: string;
  bio: string;
  expertise: string[];
  yearsExperience: number;
}

interface TeamSectionProps {
  className?: string;
}

const TeamSection = ({ className = '' }: TeamSectionProps) => {
  const [selectedMember, setSelectedMember] = useState<number | null>(null);

  const teamMembers: TeamMember[] = [
  {
    id: 1,
    name: 'Ahmed Al-Mutairi',
    nameArabic: 'أحمد المطيري',
    role: 'Founder & Operations Director',
    roleArabic: 'المؤسس ومدير العمليات',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19458974c-1763300956934.png",
    alt: 'Middle Eastern man in professional business attire with confident smile in modern office',
    bio: 'With over 15 years in logistics and customer service, Ahmed founded Almanara to transform the moving experience in Kuwait. His vision combines traditional hospitality with modern efficiency.',
    expertise: ['Logistics Management', 'Customer Relations', 'Business Strategy'],
    yearsExperience: 15
  },
  {
    id: 2,
    name: 'Fatima Al-Rashid',
    nameArabic: 'فاطمة الراشد',
    role: 'Customer Experience Manager',
    roleArabic: 'مديرة تجربة العملاء',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1459b9ea0-1763295924782.png",
    alt: 'Professional Arab woman in business hijab with warm smile at customer service desk',
    bio: 'Fatima ensures every customer interaction reflects Almanara\'s commitment to care. Her multilingual expertise bridges cultures and builds lasting relationships.',
    expertise: ['Customer Service', 'Conflict Resolution', 'Team Training'],
    yearsExperience: 10
  },
  {
    id: 3,
    name: 'Mohammed Al-Sabah',
    nameArabic: 'محمد الصباح',
    role: 'Lead Moving Specialist',
    roleArabic: 'أخصائي النقل الرئيسي',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ff43bd81-1768148774174.png",
    alt: 'Strong Arab man in Almanara uniform with safety equipment and clipboard',
    bio: 'Mohammed leads our field operations with precision and care. His expertise in handling delicate items and complex moves is unmatched in Kuwait.',
    expertise: ['Furniture Assembly', 'Packing Techniques', 'Safety Protocols'],
    yearsExperience: 12
  },
  {
    id: 4,
    name: 'Sara Al-Kandari',
    nameArabic: 'سارة الكندري',
    role: 'Quality Assurance Coordinator',
    roleArabic: 'منسقة ضمان الجودة',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14eb3207b-1763295731316.png",
    alt: 'Professional Kuwaiti woman in business attire reviewing quality checklist with tablet',
    bio: 'Sara\'s attention to detail ensures every move meets Almanara\'s high standards. She personally reviews customer feedback and implements continuous improvements.',
    expertise: ['Quality Control', 'Process Improvement', 'Customer Feedback'],
    yearsExperience: 8
  }];


  return (
    <section className={`py-16 lg:py-24 bg-background ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <div className="inline-flex items-center space-x-2 bg-secondary/10 px-4 py-2 rounded-full mb-6">
            <Icon name="UserGroupIcon" size={20} variant="solid" className="text-secondary" />
            <span className="text-sm font-body font-semibold text-secondary">Meet Our Team</span>
          </div>

          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-primary mb-6 leading-tight">
            The People Behind Your Peace of Mind
          </h2>

          <p className="text-lg font-body text-text-secondary leading-relaxed">
            Our team combines decades of experience with genuine care for every customer and every move.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {teamMembers.map((member) =>
          <div
            key={member.id}
            className="bg-card rounded-xl overflow-hidden shadow-soft hover:shadow-elevated transition-all duration-base card-hover cursor-pointer"
            onClick={() => setSelectedMember(selectedMember === member.id ? null : member.id)}>

              <div className="relative h-64 overflow-hidden">
                <AppImage
                src={member.image}
                alt={member.alt}
                className="w-full h-full object-cover" />

                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <h3 className="text-xl font-headline font-bold mb-1">{member.name}</h3>
                  <p className="text-sm font-body opacity-90">{member.nameArabic}</p>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-body font-semibold text-secondary mb-1">{member.role}</p>
                    <p className="text-xs font-body text-text-secondary">{member.roleArabic}</p>
                  </div>
                  <div className="flex items-center space-x-1 text-accent">
                    <Icon name="StarIcon" size={16} variant="solid" />
                    <span className="text-sm font-body font-semibold">{member.yearsExperience}y</span>
                  </div>
                </div>

                {selectedMember === member.id &&
              <div className="space-y-4 animate-fadeIn">
                    <p className="text-sm font-body text-text-primary leading-relaxed">
                      {member.bio}
                    </p>

                    <div>
                      <p className="text-xs font-body font-semibold text-primary mb-2">Expertise:</p>
                      <div className="flex flex-wrap gap-2">
                        {member.expertise.map((skill) =>
                    <span
                      key={`${member.id}-${skill}`}
                      className="inline-flex items-center px-2 py-1 bg-muted rounded-md text-xs font-body text-text-secondary">

                            {skill}
                          </span>
                    )}
                      </div>
                    </div>
                  </div>
              }

                <button
                className="mt-4 w-full flex items-center justify-center space-x-2 text-sm font-body font-semibold text-secondary hover:text-primary transition-colors duration-base"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedMember(selectedMember === member.id ? null : member.id);
                }}>

                  <span>{selectedMember === member.id ? 'Show Less' : 'Learn More'}</span>
                  <Icon
                  name={selectedMember === member.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={16}
                  variant="solid" />

                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default TeamSection;